import { FicheSalon } from "./FicheSalon.tsx";
import { QrStand } from "../salon/QrStand.tsx";
import { Lien } from "../lib/router.tsx";
import { PlanSalon3D } from "./PlanSalon3D.tsx";
import { useEffect, useState } from "react";
import { FormulaireAdmin, ListeAdmin, Panneau, Widget } from "./ui.tsx";
import { LISTES } from "./data.ts";
import { ENTITES, FicheEntite, FormEntite, ListeEntite } from "./entites.tsx";
import { EnCours } from "../screens/EnCours.tsx";
import { Accueil } from "../front/Accueil.tsx";
import { Comptes } from "./Comptes.tsx";
import { EDITIONS } from "../app/config.ts";
import { useApp } from "../app/state.tsx";
import type { Entite } from "@hopitech/contracts";
import { creerEntite, listerEntites, modifierEntite, statsEdition, supprimerEntite } from "../lib/api.ts";

/** Répartiteur du back-office : listes pilotées par données, écrans sur mesure sinon. */
export function AdminRoute({ chemin }: { chemin: string }): React.ReactElement {
  const seg = chemin.split("/").filter(Boolean); // ["admin", segment?, x?, y?]
  const config = seg[1] ? ENTITES[seg[1]] : undefined;
  if (config) {
    if (config.type === "salon" && (seg[2] === "nouveau" || seg[3] === "editer")) return <FicheSalon key={seg[2]} id={seg[2] === "nouveau" ? undefined : seg[2]} />;
    if (!seg[2]) return <ListeEntite key={config.type} config={config} />;
    if (seg[2] === "nouveau") return <FormEntite config={config} />;
    if (seg[3] === "editer") return <FormEntite config={config} id={seg[2]} />;
    return <FicheEntite config={config} id={seg[2]} />;
  }
  const liste = LISTES[chemin];
  if (liste) return <ListeAdmin {...liste} />;
  switch (chemin) {
    case "/admin": return <Dashboard />;
    case "/admin/salon": return <SalonForm />;
    case "/admin/plan": return <PlanVue />;
    case "/admin/presences": return <PresencesVue />;
    case "/admin/documents": return <DocumentsVue />;
    case "/admin/gamification": return <GamificationVue />;
    case "/admin/accueil": return <Accueil />;
    case "/admin/comptes": return <Comptes />;
    default: return <EnCours />;
  }
}

// ---------- D0 · Tableau de bord ----------

function Sparkbars({ valeurs }: { valeurs: readonly number[] }): React.ReactElement {
  const max = Math.max(...valeurs, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 48 }}>
      {valeurs.map((v, i) => (
        <div key={i} style={{ flex: 1, height: `${(v / max) * 100}%`, background: i === valeurs.length - 1 ? "var(--accent)" : "var(--soft)", borderRadius: 3, minHeight: 3 }} />
      ))}
    </div>
  );
}

function Dashboard(): React.ReactElement {
  const app = useApp();
  const [comptes, setComptes] = useState<Record<string, number>>({});
  useEffect(() => { statsEdition(app.edition.id).then((r) => setComptes(r.comptes)).catch(() => setComptes({})); }, [app.edition.id]);
  const n = (t: string): number => comptes[t] ?? 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 980 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
        <Widget titre="Exposants" valeur={String(n("exposant"))} ton="ok" />
        <Widget titre="Stagiaires" valeur={String(n("stagiaire"))} />
        <Widget titre="Visiteurs & invités" valeur={String(n("visiteur") + n("invite"))} />
        <Widget titre="Intervenants" valeur={String(n("intervenant") + n("conferencier"))} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Panneau titre="Trafic" sous="Plausible pour l'application (RGPD), Search Console & GA4 pour la vitrine.">
          <Sparkbars valeurs={[12, 18, 15, 22, 28, 24, 31]} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "var(--muted)" }}>
            <span>7 derniers jours</span><span className="num" style={{ fontWeight: 700, color: "var(--ink)" }}>1 240 visites</span>
          </div>
        </Panneau>
        <Panneau titre="Monétisation" sous="Depuis Sellsy — chiffre par famille de produit.">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[["Stands", "31 350 €", 100], ["Publicités", "6 500 €", 42], ["Repas", "1 120 €", 12]].map(([k, v, w]) => (
              <div key={k as string} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}><span>{k}</span><span className="num" style={{ fontWeight: 600 }}>{v}</span></div>
                <div style={{ height: 6, background: "var(--panel)", borderRadius: 999 }}><div style={{ width: `${w as number}%`, height: "100%", background: "var(--accent)", borderRadius: 999 }} /></div>
              </div>
            ))}
          </div>
        </Panneau>
      </div>

      <Panneau titre="Fidélité & récurrence" sous="Un exposant fidèle mérite peut-être un traitement particulier — l'info est là.">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[["DELABIE", "12ᵉ participation"], ["APAVE", "8ᵉ participation"], ["IDEX", "3ᵉ participation"]].map(([n, r]) => (
            <div key={n} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 10, borderBottom: "1px solid var(--line)" }}>
              <span style={{ fontWeight: 600 }}>{n}</span>
              <span className="pill pill-accent" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 12 }}>{r}</span>
            </div>
          ))}
        </div>
      </Panneau>

      <p style={{ margin: 0, fontSize: 12.5, color: "var(--muted-2)" }}>
        Filtres <strong>manuels</strong> (boutons), pas de rafraîchissement automatique — décision de la réunion du 28/07. Édition affichée : {app.edition.optionLabel}.
      </p>
    </div>
  );
}

// ---------- D2 · Fiche édition ----------

function SalonForm(): React.ReactElement {
  const app = useApp();
  return <FicheSalon key={app.edition.id} />;
}

// ---------- D3 · Plan des emplacements ----------

function PlanVue(): React.ReactElement {
  const app = useApp();
  const [exposants, setExposants] = useState<Entite[] | null>(null);
  async function charger(): Promise<void> {
    try { setExposants((await listerEntites("exposant", app.edition.id)).entites); } catch { setExposants([]); }
  }
  useEffect(() => { void charger(); /* eslint-disable-next-line */ }, [app.edition.id]);
  async function affecter(e: Entite, emplacement: string): Promise<void> {
    await modifierEntite("exposant", e.id, { ...(e.donnees as Record<string, unknown>), emplacement }, e.version);
    await charger();
  }
  const attribues = (exposants ?? []).filter((e) => (e.donnees as Record<string, unknown>).emplacement).length;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 820 }}>
      <div className="card" style={{ padding: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div><h2 className="display" style={{ margin: "0 0 6px" }}>Le salon sur grand écran</h2><p style={{ margin: 0, color: "var(--muted)" }}>Scène 3D et personnages animés. Pour le live 2026 : attribuez des emplacements uniques P01 à P48, puis affichez les QR correspondants sur les stands.</p></div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}><Lien vers="/salon/demo" className="btn btn-primary">Voir la démonstration 3D →</Lien><Lien vers="/salon/ecran?edition=11" className="btn btn-ghost">Écran live 2026 →</Lien></div>
      </div>
      <PlanSalon3D />
      <Panneau titre="Attribution des emplacements" sous="Rapprocher chaque exposant d'un emplacement réel. Le bureau place ; l'exposant a choisi son type.">
        <div style={{ display: "flex", gap: 16, fontSize: 12.5, color: "var(--muted)" }}>
          <span><strong style={{ color: "var(--ink)" }}>{attribues}</strong> attribué(s)</span>
          <span><strong style={{ color: "var(--ink)" }}>{(exposants ?? []).length - attribues}</strong> en attente</span>
        </div>
        {exposants === null && <p style={{ color: "var(--muted)" }}>Chargement…</p>}
        {exposants !== null && exposants.length === 0 && <p style={{ color: "var(--muted)", fontSize: 13 }}>Aucun exposant pour cette édition.</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(exposants ?? []).map((e) => <LignePlan key={e.id} e={e} onAffecter={(v) => affecter(e, v)} />)}
        </div>
      </Panneau>
    </div>
  );
}

function LignePlan({ e, onAffecter }: { e: Entite; onAffecter: (v: string) => Promise<void> }): React.ReactElement {
  const d = e.donnees as Record<string, unknown>;
  const [val, setVal] = useState(String(d.emplacement ?? ""));
  const [etat, setEtat] = useState<"repos" | "envoi" | "ok">("repos");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 13px", border: "1px solid var(--line)", borderRadius: 10, flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 180px", minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{String(d.enseigne ?? e.id)}</div>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>{String(d.stand ?? "—")}</div>
      </div>
      <input className="input" style={{ width: 110 }} value={val} placeholder="A-14" onChange={(ev) => { setVal(ev.target.value); setEtat("repos"); }} />
      <button className="btn btn-primary" style={{ padding: "8px 13px", fontSize: 13 }} disabled={etat === "envoi"} onClick={async () => { setEtat("envoi"); await onAffecter(val); setEtat("ok"); }}>
        {etat === "ok" ? "✓ Attribué" : etat === "envoi" ? "…" : "Attribuer"}
      </button>
      <QrStand key={`${e.id}-${e.version}`} exposant={e} />
    </div>
  );
}

// ---------- D4 · Présences ----------

function PresencesVue(): React.ReactElement {
  const app = useApp();
  const [presences, setPresences] = useState<Entite[]>([]);
  const [rapport, setRapport] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function charger(): Promise<void> {
    try { setPresences((await listerEntites("presence", app.edition.id)).entites); } catch { setPresences([]); }
  }
  useEffect(() => { void charger(); /* eslint-disable-next-line */ }, [app.edition.id]);

  async function importer(file: File): Promise<void> {
    setEnCours(true); setRapport(null);
    try {
      const texte = await file.text();
      const lignes = texte.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      let n = 0;
      for (const l of lignes) {
        const participant = (l.split(/[;,\t]/)[0] ?? "").trim();
        if (!participant || participant.toLowerCase() === "nom") continue;
        await creerEntite("presence", app.edition.id, { participant, source: "douchette" });
        n++;
      }
      setRapport(`${n} présence(s) importée(s).`);
      await charger();
    } catch (e) { setRapport(e instanceof Error ? e.message : "Import impossible."); }
    finally { setEnCours(false); }
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 940, alignItems: "start" }}>
      <Panneau titre="Import des douchettes" sous="Un émargement par ligne (nom ou identifiant, éventuellement en CSV). Chaque ligne crée une présence.">
        <label className="btn btn-primary" style={{ alignSelf: "flex-start", cursor: "pointer" }}>
          {enCours ? "Import…" : "Choisir un fichier"}
          <input type="file" accept=".csv,.txt" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void importer(f); }} />
        </label>
        {rapport && <span className="pill pill-ok" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{rapport}</span>}
      </Panneau>
      <Panneau titre="Présences enregistrées" sous={`${presences.length} pour ${app.edition.optionLabel}`}>
        {presences.length === 0 ? <p style={{ color: "var(--muted)", fontSize: 13, margin: 0 }}>Aucune présence importée.</p> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
            {presences.map((p) => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 11px", border: "1px solid var(--line)", borderRadius: 9, fontSize: 13 }}>
                <span>{String((p.donnees as Record<string, unknown>).participant ?? "")}</span>
                <span className="mono" style={{ color: "var(--faint)", fontSize: 11 }}>{(p.creeLe ?? "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </Panneau>
    </div>
  );
}

// ---------- D13 · Documents & exports ----------

function DocumentsVue(): React.ReactElement {
  const app = useApp();
  const [type, setType] = useState("exposant");
  const porteurs: [string, string][] = [["exposant", "Exposants"], ["stagiaire", "Stagiaires"], ["intervenant", "Intervenants"], ["organisateur", "Organisateurs"], ["visiteur", "Visiteurs"], ["invite", "Invités"]];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16, maxWidth: 900 }}>
      <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
        <h3 className="display" style={{ margin: 0, fontSize: 16 }}>Badges en masse</h3>
        <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>Un PDF, une page par fiche, régénéré à la demande.</p>
        <div className="field"><label>Profil</label>
          <select className="select" value={type} onChange={(e) => setType(e.target.value)}>{porteurs.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </div>
        <a className="btn btn-primary" href={`/api/badges/${type}?edition=${app.edition.id}`} style={{ alignSelf: "flex-start", textDecoration: "none" }}>Télécharger les badges (PDF)</a>
      </div>
      <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
        <h3 className="display" style={{ margin: 0, fontSize: 16 }}>Exports CSV</h3>
        <p style={{ margin: 0, fontSize: 13, color: "var(--muted)", flex: 1 }}>Chaque liste du back-office (exposants, stagiaires, visiteurs…) porte un bouton <strong>Exporter en CSV</strong> qui télécharge les fiches de l'édition.</p>
        <span className="pill pill-ok" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12 }}>Disponible sur les listes</span>
      </div>
      <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
        <h3 className="display" style={{ margin: 0, fontSize: 16 }}>Dossiers stagiaires (ZIP)</h3>
        <p style={{ margin: 0, fontSize: 13, color: "var(--muted)", flex: 1 }}>Convention + attestation + convocation assemblées — nécessite les gabarits par édition.</p>
        <span className="pill pill-warn" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12 }}>À brancher (gabarits)</span>
      </div>
    </div>
  );
}

// ---------- D14 · Gamification (admin) ----------

function GamificationVue(): React.ReactElement {
  const app = useApp();
  const [questions, setQuestions] = useState<Entite[]>([]);
  const [f, setF] = useState({ texte: "", r1: "", r2: "", r3: "", bonne: "1" });
  const maj = (k: keyof typeof f, v: string): void => setF((s) => ({ ...s, [k]: v }));

  async function charger(): Promise<void> {
    try { setQuestions((await listerEntites("question", app.edition.id)).entites); } catch { setQuestions([]); }
  }
  useEffect(() => { void charger(); /* eslint-disable-next-line */ }, [app.edition.id]);

  async function ajouter(): Promise<void> {
    if (!f.texte.trim()) return;
    await creerEntite("question", app.edition.id, { ...f });
    setF({ texte: "", r1: "", r2: "", r3: "", bonne: "1" });
    await charger();
  }
  async function supprimer(id: string): Promise<void> { await supprimerEntite("question", id); await charger(); }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 820 }}>
      <Panneau titre="Règles du jeu" sous="Décidé en réunion : +1 point au scan, +1 par bonne réponse.">
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span className="pill pill-ok" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>Scan = +1 pt</span>
          <span className="pill pill-ok" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>Bonne réponse = +1 pt</span>
          <span className="pill pill-accent" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{questions.length} question(s) — édition {app.edition.optionLabel}</span>
        </div>
      </Panneau>

      <Panneau titre="Nouvelle question" sous="Trois réponses ; indiquez la bonne. Créées par le bureau, liées au thème.">
        <div className="field"><label>Question</label><input className="input" value={f.texte} onChange={(e) => maj("texte", e.target.value)} placeholder="Quel est le débit d'air minimal d'un bloc ?" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div className="field"><label>Réponse 1</label><input className="input" value={f.r1} onChange={(e) => maj("r1", e.target.value)} /></div>
          <div className="field"><label>Réponse 2</label><input className="input" value={f.r2} onChange={(e) => maj("r2", e.target.value)} /></div>
          <div className="field"><label>Réponse 3</label><input className="input" value={f.r3} onChange={(e) => maj("r3", e.target.value)} /></div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
          <div className="field" style={{ maxWidth: 160 }}><label>Bonne réponse</label>
            <select className="select" value={f.bonne} onChange={(e) => maj("bonne", e.target.value)}><option value="1">Réponse 1</option><option value="2">Réponse 2</option><option value="3">Réponse 3</option></select>
          </div>
          <button className="btn btn-primary" onClick={() => void ajouter()}>Ajouter la question</button>
        </div>
      </Panneau>

      <Panneau titre="Questions de l'édition">
        {questions.length === 0 ? <p style={{ color: "var(--muted)", fontSize: 13, margin: 0 }}>Aucune question pour l'instant.</p> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {questions.map((q, i) => (
              <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 10 }}>
                <span className="mono" style={{ color: "var(--faint)", fontSize: 12 }}>{String(i + 1).padStart(2, "0")}</span>
                <span style={{ flex: 1, fontSize: 13.5 }}>{String((q.donnees as Record<string, unknown>).texte ?? "")}</span>
                <button className="btn btn-ghost" style={{ padding: "5px 11px", fontSize: 12 }} onClick={() => void supprimer(q.id)}>Supprimer</button>
              </div>
            ))}
          </div>
        )}
      </Panneau>
    </div>
  );
}

